import React, { FC, useMemo } from "react";

import { Modal, ModalVariant } from "@at/design-system-patterns";

import { dataAttrTestId } from "@at/formatter-utils/lib/data-attr-utils/data-attr-utils";

import { WhatIsLeasingModalContent } from "./leasing-about-modal/leasing-about-modal";
import { LeasingAllOptionsInStockModal } from "./leasing-all-options-stock-only-modal/leasing-all-options-in-stock-modal";
import { LeasingCommissionDisclosureModal } from "./leasing-commission-disclosure/leasing-commission-disclosure-modal";
import { LeasingContactUsModal } from "./leasing-contact-us/leasing-contact-us-modal";
import { LeasingFactoryOrderModal } from "./leasing-factory-order-modal/leasing-factory-order-modal";
import { LeasingFreeDeliveryModal } from "./leasing-free-delivery-modal/leasing-free-delivery-modal";
import { LeasingFreeReturnsModal } from "./leasing-free-returns-modal/leasing-free-returns-modal";
import { HowLeasingWorksModal } from "./leasing-how-it-works-modal/leasing-how-it-works-modal";
import { IModalContent } from "./leasing-modal-hub.types";
import { LeasingNoAdminFeesModal } from "./leasing-no-admin-fees-modal/leasing-no-admin-fees-modal";
import { LeasingRoadTaxModal } from "./leasing-road-tax-modal/leasing-road-tax-modal";
import { WhatYouGetModal } from "./leasing-what-you-get-modal/leasing-what-you-get-modal";
import { LeasingTechnicalSpecificationModal } from "../../../connected-components/leasing-technical-specification-modal/leasing-technical-specification-modal";
import { setCloseLeasingModal } from "../../../contexts/leasing/actions/leasing.actions";
import {
  useLeasingDispatchContext,
  useLeasingStateContext,
} from "../../../contexts/leasing/context/leasing.context";
import { LeasingModals } from "../../../contexts/leasing/types/leasing.types";
import { useTracking } from "../../../utils/tracking/use-tracking";

export const LeasingModalHub: FC = () => {
  const state = useLeasingStateContext();
  const dispatch = useLeasingDispatchContext();
  const { trackModalEvent } = useTracking();
  const {
    show,
    type,
    title,
    footerButtonText,
    variant = ModalVariant.SLIDE,
  } = state.leasingModal;

  const ModalContent = ({ type }: IModalContent) => {
    switch (type) {
      case LeasingModals.TECHNICAL_SPECIFICATIONS: {
        return <LeasingTechnicalSpecificationModal />;
      }
      case LeasingModals.ALL_OPTIONS_IN_STOCK: {
        return <LeasingAllOptionsInStockModal />;
      }
      case LeasingModals.FACTORY_ORDER: {
        return <LeasingFactoryOrderModal />;
      }
      case LeasingModals.ABOUT_LEASING: {
        return <WhatIsLeasingModalContent />;
      }
      case LeasingModals.CONTACT_US: {
        return <LeasingContactUsModal />;
      }
      case LeasingModals.FREE_RETURNS: {
        return <LeasingFreeReturnsModal />;
      }
      case LeasingModals.FREE_DELIVERY: {
        return <LeasingFreeDeliveryModal />;
      }
      case LeasingModals.NO_ADMIN_FEES: {
        return <LeasingNoAdminFeesModal />;
      }
      case LeasingModals.ROAD_TAX: {
        return <LeasingRoadTaxModal />;
      }
      case LeasingModals.HOW_LEASING_WORKS: {
        return <HowLeasingWorksModal />;
      }
      case LeasingModals.WHAT_YOU_GET_WHEN_YOU_LEASE: {
        return <WhatYouGetModal />;
      }
      case LeasingModals.COMMISSION_DISCLOSURE: {
        return <LeasingCommissionDisclosureModal />;
      }
      default: {
        return null;
      }
    }
  };

  const modalTitle = title ?? "Leasing modal";

  return useMemo(() => {
    if (!show) return null;
    return (
      <Modal
        modalHashName={type}
        hasBackLink={false}
        closeText="Back"
        variation={variant}
        isShowing={true}
        hideFn={() => {
          dispatch(setCloseLeasingModal());
        }}
        modalTitle={modalTitle}
        footerButtonText={footerButtonText}
        attributes={{ [dataAttrTestId]: modalTitle }}
        trackingCallback={({ modalAction }) => {
          trackModalEvent({
            trackingLabel: `${modalTitle}`,
            modalAction,
          });
        }}
      >
        <ModalContent type={type} />
      </Modal>
    );
  }, [
    dispatch,
    trackModalEvent,
    footerButtonText,
    modalTitle,
    type,
    variant,
    show,
  ]);
};
